import React, { forwardRef } from 'react';
import styles from './ApplicantsTable.module.css';
import moment from 'moment';
import {
  researchFullName,
  researchEntryTitle,
  researchEntryId,
  researchSubmittedOn,
  submissionKindLabel,
} from '../../../app/helpers/researchEntry';
import { researchStatusLabel } from '../../../app/helpers/researchQuery';

/**
 * The printed copy of a records table.
 *
 * A printout is a document that leaves the building — it gets filed, attached
 * to a memo, circulated. So it carries its own identity: what it is, when it
 * was taken and how many records it covers. A sheet of bare rows cannot be
 * interpreted a week later, and a reader cannot tell whether they are holding
 * all of it or the first page of ten.
 *
 * The print RULES live in `ApplicantsTable.module.css` under `@media print`;
 * the paper size is in the `pageStyle` passed to `useReactToPrint`, scoped
 * there so landscape applies to this table and not to every print in the CMS.
 */
const UsersTable = forwardRef(({ tableHeader, data, mode, title }, ref) => {
  // `data` arrives in three shapes across the modes this one component serves.
  const rows = Array.isArray(data)
    ? data
    : (data?.items ?? data?.data?.items ?? []);
  const total = Array.isArray(data)
    ? data.length
    : (data?.totalCount ?? rows.length);

  return (
    <div className={styles.table} ref={ref}>
      <div className={styles.printHeader}>
        <h2>{title}</h2>
        <div className={styles.printMeta}>
          <span>أكاديمية شرطة دبي</span>
          <span>عدد السجلات: {total}</span>
          <span>تاريخ الطباعة: {moment().format('YYYY/MM/DD — HH:mm')}</span>
        </div>
      </div>
      <div className={styles['containerPrint']}>
        <table className={styles['userstable-container']}>
          <thead>
            <tr>
              {tableHeader &&
                tableHeader?.map((item, index) => {
                  return (
                    <th
                      className={
                        index === 0
                          ? `${styles.firstRadius} ${styles['header-cell']}`
                          : index === tableHeader.length - 1
                          ? `${styles.lastRadius} ${styles['header-cell']}`
                          : `${styles['header-cell']} `
                      }
                      key={index}
                    >
                      {item?.name}
                    </th>
                  );
                })}
            </tr>
          </thead>
          <tbody>
            {mode === 'users' &&
              data?.data?.items?.map((item, index) => (
                <tr key={item.userId}>
                  <td className={`${styles['firstColumn']}`}>
                    {item.fullName_En}
                  </td>
                  <td>{item.fullName_AR}</td>
                  <td>{item.email}</td>
                  <td>{item.mobile}</td>
                  <td>{item.userRoles}</td>
                </tr>
              ))}
            {mode === 'applicants' &&
              data?.items?.map((item, index) => (
                <tr key={item.applicantId}>
                  <td className={`${styles['firstColumn']}`}>
                    {item.fullName_EN}
                  </td>
                  <td>{item.fullName_AR}</td>
                  <td>{item.email}</td>
                  <td>{item.mobile}</td>
                  <td>{item.applicationStart_Display}</td>
                </tr>
              ))}
            {mode === 'happinessMeter' &&
              data?.map((item, index) => (
                <tr key={item.applicantId}>
                  <td className={`${styles['firstColumn']}`}>
                    {item.happyMetter}
                  </td>
                  <td>
                    {moment(item.recordedDate).format('MMM Do YYYY, h:mm a')}
                  </td>
                </tr>
              ))}
            {mode === 'leads' &&
              data?.items?.map((item, index) => (
                <tr key={item.showingInterestId}>
                  <td className={`${styles['firstColumn']}`}>
                    {item.firstName}
                  </td>
                  <td>{item.lastName}</td>
                  <td>{item.email}</td>
                  <td>{item.phoneNumber}</td>
                  <td>{item.degreeType}</td>
                </tr>
              ))}
            {mode === 'newsletterSubscription' &&
              data?.items?.map((item) => (
                <tr key={item.newsletterSubscriptionId}>
                  <td className={`${styles['firstColumn']}`}>{item.email}</td>
                  <td>
                    {moment(item.subscribedOn).format('MMM D, YYYY h:mm A')}
                  </td>
                </tr>
              ))}
            {mode === 'scholarship' &&
              data?.items?.map((item, index) => (
                <tr key={item.scholarshipApplicationId}>
                  <td className={`${styles['firstColumn']}`}>
                    {item.firstName}
                  </td>
                  <td>{item.lastName}</td>
                  <td>{item.email}</td>
                  <td>{item.phoneNumber}</td>
                  <td>{item.degreeType}</td>
                  <td>
                    {moment(item.createdOn).format('MMM Do YYYY, h:mm a')}
                  </td>
                </tr>
              ))}
            {mode === 'facility' &&
              data?.items?.map((item, index) => (
                <tr key={item.facilityId}>
                  <td className={`${styles['firstColumn']}`}>
                    {item.eventTitle}
                  </td>
                  <td>{item.name}</td>
                  <td>{item.email}</td>
                  <td>{item.mobile}</td>
                  <td>{item.organizationTypeDisplay}</td>
                  <td>{item.organizationName}</td>
                  <td>
                    {moment(item.createdOn).format('MMM Do YYYY, h:mm a')}
                  </td>
                </tr>
              ))}
            {mode === 'quizzes' &&
              data?.map((item, index) => (
                <tr key={item.quizId}>
                  <td className={`${styles['firstColumn']}`}>
                    {item.userName}
                  </td>
                  <td>{item.userEmail}</td>
                  <td>
                    {item?.degreeSuggested?.degreeType == 0
                      ? 'Bachelor'
                      : item?.degreeSuggested?.degreeType == 1
                      ? 'Master'
                      : 'PHD'}
                  </td>
                  <td>{item?.degreeSuggested?.degreeNameEn}</td>
                </tr>
              ))}

            {/* These SEVEN columns must stay in step with `researchTableHeader`
                in ResearchesPage.jsx and with the on-screen rows in
                ApplicantsTable.jsx. They were left on the old six — firstName,
                lastName, researchTypeDisplay, createdOn — when the inbox became
                a registry, so the printout had seven headings above six cells:
                every column after the first sat under the wrong heading, and
                the two name fields printed blank because the API no longer
                returns them. */}
            {mode === 'research' &&
              rows.map((item) => {
                const submitted = researchSubmittedOn(item);
                return (
                  <tr key={researchEntryId(item)}>
                    <td className={`${styles['firstColumn']}`}>
                      {researchFullName(item)}
                    </td>
                    {/* Status, not phone. The screen table swapped them when
                        the workflow statuses arrived, and these seven cells
                        must stay in step with `researchTableHeader` — seven
                        headings above six matching cells is how the last
                        misalignment got shipped. */}
                    <td>{researchStatusLabel(item.status)}</td>
                    <td>{submissionKindLabel(item.submissionKind)}</td>
                    <td>{researchEntryTitle(item)}</td>
                    <td>{item.publicationYear || '-'}</td>
                    <td>{item.email}</td>
                    <td>
                      {submitted
                        ? moment(submitted).format('YYYY/MM/DD')
                        : '-'}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
});
export default UsersTable;
